import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  BundledFeatures,
  BundledFeaturesStatus,
} from '../domain/subscription/bundled_features';

export interface BundledFeaturesSchema {
  code: string;
  label: string;
  stripeProductId: string;
  status: BundledFeaturesStatus;
  createdAt: Timestamp;
  updatedAt?: Timestamp | null;
  sort: number;
  features: string[];
  details: string[];
}

@Injectable()
export class BundledFeaturesRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<BundledFeaturesSchema>(
      'bundled_features',
      queryFn,
    );
  }

  toSchema(entity: BundledFeatures): BundledFeaturesSchema {
    return {
      code: entity.code,
      label: entity.label,
      sort: entity.sort,
      stripeProductId: entity.stripeProductId ?? null,
      createdAt:
        entity.createdAt !== undefined
          ? Timestamp.fromDate(entity.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: entity.status,
      features: entity.features,
      details: entity.details,
    };
  }

  fromSchema(schema: BundledFeaturesSchema, id: string): BundledFeatures {
    return BundledFeatures.create(
      {
        code: schema.code,
        label: schema.label,
        sort: schema.sort,
        stripeProductId: schema.stripeProductId,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt?.toDate() ?? undefined,
        status: schema.status,
        features: schema.features,
        details: schema.details,
      },
      new UniqueEntityID(id),
    );
  }

  async findAllActive(): Promise<BundledFeatures[]> {
    try {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', BundledFeaturesStatus.Active)
            .orderBy('sort', 'asc'),
        ).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } catch (e) {
      console.error('❌ findAllActive', e);
      throw e;
    }
  }

  async findAllActiveByFeature(featureId: string): Promise<BundledFeatures[]> {
    try {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', BundledFeaturesStatus.Active)
            .where('features', 'array-contains', featureId)
            .orderBy('sort', 'asc'),
        ).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } catch (e) {
      console.error('❌ findAllActiveByFeature', e);
      throw e;
    }
  }
}
